import { AsyncPipe, CurrencyPipe, DatePipe, formatDate, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, input, Input, InputSignal, OnInit, Output, output, OutputEmitterRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { first, map, Observable, of, switchMap, tap, timer } from 'rxjs';
import { MedicalService } from '../../../services/medical.service';
import { ButtonGroupModule } from "primeng/buttongroup";
import { TableModule } from "primeng/table";
import { InputNumberModule } from "primeng/inputnumber";
import { CalendarModule } from "primeng/calendar";
import { ChipModule } from "primeng/chip";
import { FieldsetModule } from "primeng/fieldset"
import { CardModule } from "primeng/card";
import { FloatLabelModule } from "primeng/floatlabel";
import { DropdownChangeEvent, DropdownModule } from "primeng/dropdown";
import { FormsModule, NgForm } from '@angular/forms';
import { FacilityLocationSearchComponent } from '../facilities/facility-search/facility-location-search.component';
import { ModifierSelectorComponent } from '../procedures/modifier-selector/modifier-selector.component';
import { DiagnosisSelectorComponent } from '../procedures/diagnosis-selector/diagnosis-selector.component';
import { ProcedureSelectorComponent } from '../procedures/procedure-selector/procedure-selector.component';
import { PartySearchComponent } from '../../parties/party-search/party-search.component';
import { AddressEditorComponent } from '../../locations/address-editor/address-editor.component';
import { PhoneNumberEditorComponent } from '../../phone-numbers/phone-number-editor/phone-number-editor.component';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { instanceToInstance } from 'class-transformer';
import { DraftMedicalBill, DraftMedicalBillLine, MedicalBillSubmission } from '../../../models/medical-bill';
import { Encounter } from '../../../models/encounter';
import { UtilService } from '../../../services/util.service';
import { PartyService } from '../../../services/party.service';
import { LocationService } from '../../../services/location.service';
import { MedicalState } from '../../../state/medical/medical-state';
import { MedicalStateActions } from '../../../state/medical/medical-actions';
import { Diagnosis, ICategory } from '../../../models/procedure';
import { AddressInsert } from '../../../models/address';
import { IParty } from '../../../models/party';
import { IPhone, PhoneInsert } from '../../../models/phone';
import { ILocation, Location } from '../../../models/location';
import { FacilityLocation } from '../../../models/facility';


@Component({
  selector: 'lib-draft-medical-bill',
  standalone: true,
  imports: [ImageModule, AsyncPipe, NgIf, ButtonModule, FieldsetModule, FormsModule, FacilityLocationSearchComponent,
    ModifierSelectorComponent, DiagnosisSelectorComponent, ProcedureSelectorComponent, TooltipModule, CurrencyPipe,
    DialogModule, ButtonGroupModule, TableModule, InputNumberModule, CalendarModule, TagModule, BadgeModule,
    ChipModule, CardModule, FloatLabelModule, DropdownModule, PartySearchComponent, InputTextModule, NgFor, 
    AddressEditorComponent, PhoneNumberEditorComponent, DatePipe],
  templateUrl: './draft-medical-bill.component.html',
  styleUrl: './draft-medical-bill.component.scss'
})
export class DraftMedicalBillComponent implements OnInit
{

  draftMedicalBill: InputSignal<DraftMedicalBill | undefined> = input<DraftMedicalBill>();
  medicalBill: DraftMedicalBill | undefined;
  medicalBillSubmission: InputSignal<MedicalBillSubmission> = input.required<MedicalBillSubmission>();
  @Output() saved : EventEmitter<DraftMedicalBill> = new EventEmitter<DraftMedicalBill>();
  @Output() cancelled : EventEmitter<void> = new EventEmitter<void>();



  thumbnailImage?: string;
  billPDf?: SafeResourceUrl;
  submissionStatus$? : Observable<string | undefined>;
  isProcessing$? : Observable<boolean>;
  showViewDialog: boolean = false;
  showPatientAddressEditor: boolean = false;
  showInsuredAddressEditor: boolean = false;
  showPatientPhoneNumberEditor: boolean = false;
  backupDraftBill?: DraftMedicalBill;
  encounters: Encounter[] = [];
  selectedEncounter: Encounter | undefined;
  isDuplicate: boolean = false;

  @ViewChild('billForm') billForm: NgForm | undefined;



  constructor
  (
    private _store: Store,
    private _util: UtilService,
    private _sanitizer: DomSanitizer,
    private _medical: MedicalService,
    private _partyService: PartyService,
    private _location: LocationService
  )
  {}

  async ngOnInit() 
  {
    this.submissionStatus$ = this._store.select(MedicalState.submissionState).pipe(map(f => f(this.medicalBillSubmission().id)))
    this.isProcessing$ = this._store.select(MedicalState.submissionProcessing).pipe(map(f => f(this.medicalBillSubmission().id)))

    //Get the thumbnail
    this.thumbnailImage = await this._util.getFileImageURL(this.medicalBillSubmission().billThumbnail);
    const rawURL = await this._util.getFileImageURL(this.medicalBillSubmission().billImage);
    this.billPDf = this._sanitizer.bypassSecurityTrustResourceUrl(`${rawURL}#view=FitH&navpanes=0&zoom=100 `);

    this.medicalBill = this.draftMedicalBill();
    if(this.medicalBill && this.medicalBill.patient)
    {
      await this.loadParty(this.medicalBill.patient.id);
      await this.loadEncountersForPatient();

      //Check whether this bill is a duplicate
      this.isDuplicate = await this._medical.isBillDuplicate(this.medicalBill.patientAccountNumber ?? "", this.billDateOfService ?? new Date(), this.medicalBill.totalLinesCost ?? 0, this.medicalBill.lines.map(l => l.procedure?.code ?? ""));
    }


    //This is where we'll continue to check for whether this bill is a duplicate or not
    if(this.billForm && this.billForm.valueChanges)
    {
      this.billForm.valueChanges
      .pipe
      (
        // first(),
        // switchMap(values => {return of(values)})
        map
        (
          async values => 
          {
            console.log("Form values changed", values);
            if(this.medicalBill)
            {
              this.isDuplicate = await this._medical.isBillDuplicate(values?.patientAccountNumber ?? "", values?.serviceDate ?? new Date(), values?.totalCost ?? 0, this.medicalBill.lines.map(l => l.procedure?.code ?? ""))
            }
          }
        )
          
      ).subscribe();
    }
  }

  async procedureChanged()
  {
    //Wait for the form to update with the new procedure values before checking for duplicates
    timer(500).subscribe(async () => {
      if(this.medicalBill)
      {
        this.isDuplicate = await this._medical.isBillDuplicate(this.medicalBill.patientAccountNumber ?? "", this.billDateOfService ?? new Date(), this.medicalBill.totalLinesCost ?? 0, this.medicalBill.lines.map(l => l.procedure?.code ?? ""));
      }
    });
  }

  deleteBill()
  {
    if(this.medicalBill && this.medicalBill.id)
      this._medical.deleteDraftMedicalBill(this.medicalBill.id)
  }

  refreshButtonPressed()
  {
    this._store.dispatch(new MedicalStateActions.StartProcessingMedicalBillSubmission({submission:this.medicalBillSubmission()}))
    
    this._medical.processSubmission(this.medicalBillSubmission().id)
  }

  get billCategory() : ICategory | undefined
  {
    if(this.medicalBill && this.medicalBill.lines.length > 0 && this.medicalBill.lines[0].procedure)
    {
      return this.medicalBill.lines[0].procedure.category;
    }
    return undefined;
  }

  get billDateOfService() : Date | undefined
  {
    if(this.medicalBill && this.medicalBill.lines.length > 0 && this.medicalBill.lines[0].dateOfService)
    {
      return this.medicalBill.lines[0].dateOfService;
    }
    return undefined;
  }

  get upperIdentifier() : string
  {
    const draft = this.draftMedicalBill();
    const submission = this.medicalBillSubmission();
    if(draft)
    {
      if(draft.patient)
      {
        return `Patient: ${draft.patient.fullName}`;
      }
      else
      {
        const submittedOn = draft.submissionDate ?? draft.billSubmission.dateSubmitted;
        return `Draft Submitted on ${formatDate(submittedOn,"MM/dd/yyyy","en-US")}`
      }
    }
    else if(submission)
    {
      return `Draft Submitted on ${formatDate(submission.dateSubmitted,"MM/dd/yyyy","en-US")}`
    }
    return "Submision"

  }

  get lowerIdentifier() : string
  {
    const draft = this.draftMedicalBill();
    const submission = this.medicalBillSubmission();
    if(draft)
    {
      if (draft.servicingProvider)
      {
        return `${draft.servicingProvider.facility.name}`
      }
    }
    return "";
  }

  genders: {display:string,value:string}[] = [{display:"Male",value:"M"},{display:"Female",value:"F"}];

  async loadEncountersForPatient()
  {
    if(this.medicalBill && this.medicalBill.patient)
      this.encounters = (await this._medical.getEligibleEncountersForPatient(this.medicalBill.patient.id)).toSorted((a,b) => (b.getLatestAppointment()?.appointmentDate.getTime() ?? 0) - (a.getLatestAppointment()?.appointmentDate.getTime() ?? 0));
  }

  async loadParty(patientId: number )
  {
    if(this.medicalBill)
      this.medicalBill.patient = await this._partyService.getParty(patientId);
  }

  saveButtonPressed(event: MouseEvent)
  {
    //Confirm a Medical Bill
    if (this.medicalBill && this.selectedEncounter )
    {
      const mbi = this.medicalBill.createMedicalBillInsert();
      if(mbi)
      {
        this._medical.confirmDraftMedicalBill(this.medicalBill,mbi,this.selectedEncounter.id)
        this.saved.emit(this.draftMedicalBill());
      }
    }
  }

  cancelButtonPressed(event: MouseEvent)
  {
    this.medicalBill = instanceToInstance(this.backupDraftBill);
    this.backupDraftBill = undefined;
    this.showViewDialog = false;
    this.cancelled.emit();
  }
  
  startEditing()
  {
    this.backupDraftBill = instanceToInstance(this.draftMedicalBill());
    this.showViewDialog = true;
  }

  allowSave() : boolean
  {
    return this.formErrors.every(v => v === true) && !this.isDuplicate; 
  }

  get formErrors() : boolean[]
  {
    if(this.medicalBill)
    return [this.medicalBill.patient != null , this.medicalBill.insuredName != null , 
          this.medicalBill.insuredAddress?.street1 != null , this.medicalBill.insuredAddress?.city != null, 
          this.medicalBill.insuredPhone != null , this.medicalBill.insuredAddress?.state != null,
          this.medicalBill.insuredAddress?.zipCode != null,
          this.medicalBill.patientAddress != null , this.medicalBill.phoneNumber != null ,
          this.medicalBill.servicingProvider != null ,
          this.medicalBill.patientAccountNumber != null , 
          this.medicalBill.billingProvider != null , this.medicalBill.lines != null , this.medicalBill.lines.length >= 1
          && this.allCostsSet() , this.allProceduresSet() , this.allServiceDatesSet() , this.allDiagnosesSet(), this.allUnitsSet(), this.selectedEncounter != null]
    return [];
  }

  get formErrorCount() : number
  {
    return this.formErrors.filter(f => f === false).length;
  }

  clearPatient(event:MouseEvent)
  {
    if(this.medicalBill)
      this.medicalBill.patient = undefined;
  }

  openNewPatientAddressDialog()
  {
    this.showPatientAddressEditor = true;
  }

  openNewInsuredAddressDialog()
  {
    this.showInsuredAddressEditor = true;
  }

  async onPatientAddressSave(address: ILocation)
  {
    if (!this.medicalBill || !this.medicalBill.patient)
      return;

    const newAddress = { id: 0, addressType: 0, location: address };
    const ai = new AddressInsert(newAddress);
    const dbAddress = await this._partyService.addAddress(this.medicalBill.patient.id, ai);

    if (!dbAddress)
      return;

    this.medicalBill.patient.addresses = [
      ...(this.medicalBill.patient.addresses || []),
      dbAddress
    ];

    this.medicalBill.patientAddress = dbAddress.location;
    this.showPatientAddressEditor = false;
  }



  async onInsuredAddressSave(address: ILocation)
  {
    if (!this.medicalBill)
      return;

    const newAddress = { id: 0, addressType: 0, location: address };
    const ai = new AddressInsert(newAddress);
    const insertedAddress = await this._location.addLocation(ai);

    if (!insertedAddress)
      return;

    this.medicalBill.insuredAddress = insertedAddress;

    this.showInsuredAddressEditor = false;
  }


  onInsuredAddressCancel()
  {
    this.showInsuredAddressEditor = false;
  }

  onPatientAddressCancel()
  {
    this.showPatientAddressEditor = false;
  }

    openNewPatientPhoneNumberDialog()
  {
    this.showPatientPhoneNumberEditor = true;
  }

  async onPatientPhoneNumberSave(phone: IPhone)
  {
    if (!this.medicalBill || !this.medicalBill.patient)
      return;

    const pi = new PhoneInsert(phone);
    const dbPhone = await this._partyService.addPhone(this.medicalBill.patient.id, pi);

    if (!dbPhone)
      return;

    this.medicalBill.patient.phones = [
      ...(this.medicalBill.patient.phones || []),
      dbPhone
    ];

    this.medicalBill.phoneNumber = dbPhone.phoneNumber;
    this.showPatientPhoneNumberEditor = false;
  }

  onPatientPhoneNumberCancel()
  {
    this.showPatientPhoneNumberEditor = false;
  }

  async patientSelected(patient: IParty)
  {
      //Get the addresses and whatnot for the person
      await this.loadParty(patient.id);
      await this.loadEncountersForPatient();

  }

  patientAddressChanged(event: DropdownChangeEvent)
  {
    //If we have no insured address...
    if(this.medicalBill && !this.medicalBill.insuredAddress)
    {
      this.medicalBill.insuredAddress = event.value;
    }
  }

  servicingProviderChange(provider:FacilityLocation)
  {
    if(this.medicalBill)
      this.medicalBill.servicingProvider = provider;
  }

  clearServicingProvider(event: MouseEvent)
  {
    if(this.medicalBill)
      this.medicalBill.servicingProvider = undefined;
  }

  billingProviderChange(provider:FacilityLocation)
  {
    if(this.medicalBill)
      this.medicalBill.billingProvider = provider;
  }

  clearBillingProvider(event:MouseEvent)
  {
    if(this.medicalBill)
      this.medicalBill.billingProvider = undefined;
  }

  addLine(event:MouseEvent)
  {
    if(this.medicalBill)
    {
      if(!this.medicalBill.lines)
      {
        this.medicalBill.lines = [];
      }

      var draftLine = {} as DraftMedicalBillLine;
      draftLine.processingErrors = [];
      this.medicalBill.lines = [...this.medicalBill.lines, draftLine ]
    }
  }

  removeLine(line: DraftMedicalBillLine)
  {
    if(this.medicalBill)
      this.medicalBill.lines = [...this.medicalBill.lines.filter(l => l != line)]
  }

  diagnosesSet(diagnoses: Diagnosis[])
  {
    return diagnoses.map(d => d.code).join(', ');
  }

  allProceduresSet() : boolean
  {
    return this.medicalBill?.lines.every(l=>l.procedure) || false
  }

  allDiagnosesSet() : boolean
  {
    return this.medicalBill?.lines.every(l=>l.associatedDiagnosis && l.associatedDiagnosis.length > 0) || false
  }

  allCostsSet() : boolean
  {
    return this.medicalBill?.lines.every(l=>l.cost) || false
  }
  allUnitsSet() : boolean
  {
    return this.medicalBill?.lines.every(l=>l.units) || false
  }
  allServiceDatesSet() : boolean
  {
    return this.medicalBill?.lines.every(l=>l.dateOfService) || false
  }
} 
